import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CartSheet from "../components/CartSheet";
import ProductReviews from "../components/ProductReviews";
import api from "../lib/api";
import { getIcon } from "../lib/icons";
import { useCart } from "../lib/CartContext";
import { useLang } from "../lib/i18n";
import { toast } from "sonner";
import { ChevronLeft, Minus, Plus, ShoppingCart, PawPrint } from "lucide-react";
import { SITE_URL, setMeta, setCanonical, setJsonLd, removeJsonLd } from "../lib/seo";

const HOME_TITLE = "Pet Munchies Egypt | Online Pet Store for Cats & Dogs";
const HOME_DESCRIPTION = "Shop cat and dog food, treats, litter and pet supplies online at Pet Munchies Egypt. Same-day delivery in Cairo, Giza & nearby areas.";

export default function ProductDetail() {
  const { slug } = useParams();
  const { addItem } = useCart();
  const { t } = useLang();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [qty, setQty] = useState(1);
  const [genuineRating, setGenuineRating] = useState({ avg: 0, count: 0 });

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setProduct(null);
    api
      .get(`/products/${slug}`)
      .then((r) => setProduct(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    api
      .get(`/products/${slug}/reviews`)
      .then((r) => {
        const genuine = (r.data.reviews || []).filter((rv) => !rv.is_sample);
        const count = genuine.length;
        const avg = count ? genuine.reduce((s, rv) => s + rv.rating, 0) / count : 0;
        setGenuineRating({ avg: Math.round(avg * 10) / 10, count });
      })
      .catch(() => setGenuineRating({ avg: 0, count: 0 }));
  }, [slug]);

  useEffect(() => {
    if (!product) return;
    const canonicalUrl = `${SITE_URL}/products/${product.slug || slug}`;
    const title = `${product.name} | Pet Munchies Egypt`;
    const categoryLabel = t(`categories.labels.${product.category}`) || product.category;
    const petTypeLabel = product.pet_type === "dog" ? "dogs" : product.pet_type === "cat" ? "cats" : "cats & dogs";
    const description = `${product.name} — ${product.description ? product.description + " " : ""}${categoryLabel} for ${petTypeLabel} at Pet Munchies Egypt. Same-day delivery in Cairo, Giza & nearby areas.`.slice(0, 300);
    const imageUrl = product.image_url ? `${process.env.REACT_APP_BACKEND_URL}${product.image_url}` : `${SITE_URL}/logo512.png`;

    document.title = title;
    setMeta("description", description);
    setMeta("robots", "index, follow");
    setCanonical(canonicalUrl);
    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:image", imageUrl, true);
    setMeta("og:url", canonicalUrl, true);
    setMeta("og:type", "product", true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", imageUrl);

    const hasStock = typeof product.stock === "number";
    const isPurchasable = hasStock ? product.stock > 0 : !!product.in_stock;

    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      description: product.description || description,
      image: imageUrl,
      url: canonicalUrl,
      ...(product.brand ? { brand: { "@type": "Brand", name: product.brand } } : {}),
      offers: {
        "@type": "Offer",
        url: canonicalUrl,
        priceCurrency: "EGP",
        price: product.price,
        availability: isPurchasable ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        seller: { "@type": "Organization", name: "Pet Munchies" },
      },
      ...(genuineRating.count > 0
        ? { aggregateRating: { "@type": "AggregateRating", ratingValue: genuineRating.avg, reviewCount: genuineRating.count } }
        : {}),
    };
    setJsonLd("product-jsonld", jsonLd);

    return () => {
      document.title = HOME_TITLE;
      setMeta("description", HOME_DESCRIPTION);
      setMeta("og:title", HOME_TITLE, true);
      setMeta("og:description", HOME_DESCRIPTION, true);
      setMeta("og:url", `${SITE_URL}/`, true);
      setMeta("og:type", "website", true);
      setCanonical(`${SITE_URL}/`);
      removeJsonLd("product-jsonld");
    };
  }, [product, genuineRating, slug, t]);

  if (loading) {
    return (
      <div data-testid="product-detail-page">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center text-slate-500">Loading…</div>
        <Footer />
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <div data-testid="product-detail-page">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-24 text-center" data-testid="product-not-found">
          <PawPrint size={48} className="mx-auto text-slate-300" />
          <h1 className="font-display text-3xl font-bold mt-4">{t("pdp.not_found")}</h1>
          <p className="text-slate-500 mt-2">{t("pdp.not_found_desc")}</p>
          <Link to="/" className="neo-btn inline-flex items-center gap-2 px-4 py-2 mt-6 bg-white" data-testid="product-not-found-back-link">
            <ChevronLeft size={16} /> {t("pdp.back")}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const Icon = getIcon(product.icon);
  const imgSrc = product.image_url ? `${process.env.REACT_APP_BACKEND_URL}${product.image_url}` : null;
  const hasStock = typeof product.stock === "number";
  const stockNum = hasStock ? product.stock : null;
  const isPurchasable = hasStock ? stockNum > 0 : !!product.in_stock;

  const specs = [
    { label: t("pdp.brand"), value: product.brand },
    { label: t("pdp.size"), value: product.size },
    { label: t("pdp.weight"), value: product.weight },
    { label: t("pdp.age_stage"), value: product.age_stage },
  ].filter((s) => s.value);

  return (
    <div data-testid="product-detail-page">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="product-back-link">
          <ChevronLeft size={16} /> {t("pdp.back")}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-4">
          <div
            className="w-full aspect-square rounded-2xl flex items-center justify-center overflow-hidden neo-card"
            style={{ background: product.color, border: "2px solid #0F172A" }}
          >
            {imgSrc ? (
              <img src={imgSrc} alt={product.name} className="w-full h-full object-contain p-6" data-testid="product-detail-image" />
            ) : (
              <Icon size={140} className="text-white" strokeWidth={1.5} />
            )}
          </div>

          <div>
            {product.brand && (
              <div className="text-xs uppercase tracking-wider font-bold text-slate-500" data-testid="product-detail-brand">
                {product.brand}
              </div>
            )}
            <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1" data-testid="product-detail-name">
              {product.name}
            </h1>
            <div className="text-sm uppercase tracking-wider font-semibold text-slate-500 mt-2">
              {t(`categories.labels.${product.category}`)}
            </div>
            <p className="text-slate-600 mt-4" data-testid="product-detail-description">{product.description}</p>

            <div className="font-display text-3xl font-bold mt-5" data-testid="product-detail-price">
              {product.price.toFixed(0)} <span className="text-base">EGP</span>
            </div>

            {!isPurchasable && (
              <div
                className="mt-2 inline-block text-xs font-bold px-2 py-1 rounded-md"
                style={{ background: "#F87171", border: "2px solid #0F172A" }}
                data-testid="product-detail-out-of-stock"
              >
                {t("products.out_of_stock")}
              </div>
            )}

            <div className="mt-5 flex items-center gap-3">
              <div className="inline-flex items-center neo-flat overflow-hidden">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-2" data-testid="product-detail-qty-dec">
                  <Minus size={14} />
                </button>
                <span className="px-3 text-sm font-semibold" data-testid="product-detail-qty-val">{qty}</span>
                <button onClick={() => setQty((q) => Math.min(99, q + 1))} className="px-3 py-2" data-testid="product-detail-qty-inc">
                  <Plus size={14} />
                </button>
              </div>
              <button
                disabled={!isPurchasable}
                onClick={() => {
                  addItem(product, qty);
                  toast.success(`${product.name} ${t("cart.added")}`);
                }}
                className="neo-btn flex-1 px-4 py-3 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: "var(--pm-dog)" }}
                data-testid="product-detail-add-to-cart"
              >
                <ShoppingCart size={18} /> {t("products.add_to_cart")}
              </button>
            </div>

            {specs.length > 0 && (
              <div className="mt-8 neo-flat p-4" data-testid="product-detail-specs">
                <div className="font-display font-bold text-sm mb-2">{t("pdp.details")}</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {specs.map((s) => (
                    <div key={s.label} className="flex justify-between gap-2 border-b border-dashed border-slate-200 py-1">
                      <span className="text-slate-500">{s.label}</span>
                      <span className="font-semibold text-end">{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {product.ingredients && (
              <div className="mt-4 neo-flat p-4" data-testid="product-detail-ingredients">
                <div className="font-display font-bold text-sm mb-1">{t("pdp.ingredients")}</div>
                <p className="text-sm text-slate-600">{product.ingredients}</p>
              </div>
            )}

            {product.feeding_instructions && (
              <div className="mt-4 neo-flat p-4" data-testid="product-detail-feeding">
                <div className="font-display font-bold text-sm mb-1">{t("pdp.feeding")}</div>
                <p className="text-sm text-slate-600">{product.feeding_instructions}</p>
              </div>
            )}
          </div>
        </div>

        <ProductReviews slug={slug} />
      </main>
      <Footer />
      <CartSheet />
    </div>
  );
}
